'use client'
import captionStyles from './captions.module.css';
import styles from './video-layout.module.css';
import { Captions, Controls, Gesture, PlayButton, useMediaState } from '@vidstack/react';
import * as Buttons from './buttons';
import * as Menus from './menus';
import * as Sliders from './sliders';
import { TimeGroup } from './time-group';
import { Title } from './title';

export interface VideoLayoutProps {
    thumbnails?: string;
}

export default function PopupVideoLayout({ thumbnails }: VideoLayoutProps) {
    const isStarted = useMediaState('started');

    return (
        <>
            <Gestures />
            <CenteredPlayButton />
            <Captions
                className={`${captionStyles.captions} media-preview:opacity-0 media-controls:bottom-[85px] media-captions:opacity-100 absolute inset-0 bottom-2 z-10 select-none break-words opacity-0 transition-[opacity,bottom] duration-300`}
            />
            {isStarted && (
            <Controls.Root
                className={`${styles.controls} media-controls:opacity-100 text-white absolute inset-0 z-10 flex h-full w-full flex-col bg-gradient-to-t from-black/10 to-transparent opacity-0 transition-opacity`}
            >
                <div className="flex-1" />
                <Controls.Group className="flex w-full items-center px-2">
                    <Sliders.Time thumbnails={thumbnails} />
                </Controls.Group>
                <Controls.Group className="-mt-0.5 flex w-full items-center px-2 pb-2">
                    <Buttons.Play tooltipPlacement="top start" />
                    <Buttons.Mute tooltipPlacement="top" />
                    <Sliders.Volume />
                    <TimeGroup />
                    <Title />
                    <div className="flex-1" />
                    <Buttons.Caption tooltipPlacement="top" />
                    <Buttons.PIP tooltipPlacement="top" />
                    <Menus.Settings placement="top end" tooltipPlacement="top" />
                    <Buttons.Fullscreen tooltipPlacement="top end" />
                </Controls.Group>
                <div className="absolute bottom-0 start-0 w-full h-[24rem] video-modal-layout-shadow -z-10"></div>
            </Controls.Root>
            )}
        </>
    )
}

function CenteredPlayButton() {
    const isPaused = useMediaState('paused');
    const isStarted = useMediaState('started');

    if (!isPaused || isStarted) return null;

    return (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            <PlayButton className="relative w-[8rem] h-[8rem] rounded-full transition-transform ease-in-out duration-300 hover:scale-105 pointer-events-auto cursor-pointer group">
                <div
                    className="absolute inset-0 rounded-full"
                    style={{
                        background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0))',
                        padding: '2px'
                    }}
                >
                    <div className="w-full h-full rounded-full bg-white/5 backdrop-blur-[.5rem] flex items-center justify-center group-hover:bg-white/10 transition-colors">
                        <svg className="w-[3.25rem] h-[3.875rem] translate-x-[.25rem] translate-y-[.3rem]" xmlns="http://www.w3.org/2000/svg" width="34" height="39" viewBox="0 0 34 39" fill="none">
                            <path d="M30 15.5007C30 16.392 29.5385 17.2186 28.778 17.6916L7.978 30.6077C7.55712 30.8692 7.07937 31 6.6 31C6.16612 31 5.73225 30.8934 5.339 30.6755C4.5135 30.2202 4 29.3564 4 28.4168V2.5847C4 1.64506 4.5135 0.7813 5.339 0.326009C6.16612 -0.13251 7.17687 -0.105064 7.978 0.393819L28.778 13.3099C29.5385 13.7829 30 14.6112 30 15.5007Z" fill="white"/>
                        </svg>
                    </div>
                </div>
            </PlayButton>
        </div>
    );
}

function Gestures() {
    return (
        <>
            <Gesture
                className="absolute inset-0 z-0 block h-full w-full"
                event="pointerup"
                action="toggle:paused"
            />
            <Gesture
                className="absolute inset-0 z-0 block h-full w-full"
                event="dblpointerup"
                action="toggle:fullscreen"
            />
            <Gesture
                className="absolute left-0 top-0 z-10 block h-full w-1/5"
                event="dblpointerup"
                action="seek:-10"
            />
            <Gesture
                className="absolute right-0 top-0 z-10 block h-full w-1/5"
                event="dblpointerup"
                action="seek:10"
            />
        </>
    );
}